if (!customElements.get('navigation-menu-2')) {
  class NavigationMenu2 extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('.navigation-2__container');
      this.drawer = this.querySelector('.navigation-2__drawer');
      this.summary = this.details?.querySelector(':scope > summary');
      this.closeButton = this.querySelector('.navigation-2__close-button');
      this.overlay = this.querySelector('.navigation-2__overlay');
      this.headerSection = this.closest('.section-header');
      this.parentItems = Array.from(this.querySelectorAll('.navigation-2__menu-item--parent'));
      this.menu = this.querySelector('.navigation-2__menu');
      this.scrollIndicators = Array.from(this.querySelectorAll('.navigation-2__scroll-indicator'));
      this.scrollTargets = [
        this.menu,
        ...this.parentItems.map((item) => item.querySelector('.navigation-2__submenu-content')),
      ].filter(Boolean);
      this.desktopMedia = window.matchMedia('(min-width: 990px)');

      this.handleDetailsToggle = this.handleDetailsToggle.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleDrawerMouseLeave = this.handleDrawerMouseLeave.bind(this);
      this.handleBreakpointChange = this.handleBreakpointChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleScrollIndicatorClick = this.handleScrollIndicatorClick.bind(this);

      this.details?.addEventListener('toggle', this.handleDetailsToggle);
      this.closeButton?.addEventListener('click', () => this.closeMenu());
      this.overlay?.addEventListener('click', () => this.closeMenu());
      this.addEventListener('keydown', this.handleKeydown);
      this.drawer?.addEventListener('mouseleave', this.handleDrawerMouseLeave);
      this.desktopMedia.addEventListener('change', this.handleBreakpointChange);
      this.scrollTargets.forEach((target) => target.addEventListener('scroll', this.handleScroll, { passive: true }));
      this.scrollIndicators.forEach((indicator) =>
        indicator.addEventListener('click', this.handleScrollIndicatorClick),
      );

      this.parentItems.forEach((item) => this.bindParentItem(item));
      this.syncSubmenuVisibility();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateScrollIndicators());
        this.scrollTargets.forEach((target) => this.resizeObserver.observe(target));
        this.querySelectorAll('.navigation-2__panel-content').forEach((panel) => this.resizeObserver.observe(panel));
      }

      requestAnimationFrame(() => this.updateScrollIndicators());
    }

    disconnectedCallback() {
      this.details?.removeEventListener('toggle', this.handleDetailsToggle);
      this.removeEventListener('keydown', this.handleKeydown);
      this.drawer?.removeEventListener('mouseleave', this.handleDrawerMouseLeave);
      this.desktopMedia?.removeEventListener('change', this.handleBreakpointChange);
      this.scrollTargets?.forEach((target) => target.removeEventListener('scroll', this.handleScroll));
      this.scrollIndicators?.forEach((indicator) =>
        indicator.removeEventListener('click', this.handleScrollIndicatorClick),
      );
      this.resizeObserver?.disconnect();
      this.headerSection?.classList.remove('menu-open');
      document.body.classList.remove('overflow-hidden-navigation-2');
    }

    isDesktop() {
      return this.desktopMedia.matches;
    }

    handleBreakpointChange() {
      this.closeSubmenus();
      this.syncSubmenuVisibility();
      requestAnimationFrame(() => this.updateScrollIndicators());
    }

    syncSubmenuVisibility() {
      this.parentItems.forEach((item) => {
        const submenu = item.querySelector('.navigation-2__submenu');
        if (submenu) submenu.hidden = this.isDesktop() && !item.classList.contains('is-open');
      });
    }

    bindParentItem(item) {
      const link = item.querySelector('.navigation-2__menu-link');
      const toggle = item.querySelector('.navigation-2__submenu-toggle');
      const backButton = item.querySelector('.navigation-2__submenu-back');

      toggle?.addEventListener('click', () => {
        if (this.isDesktop()) {
          this.openSubmenu(item);
          return;
        }

        this.toggleSubmenu(item);
      });

      link?.addEventListener('click', (event) => {
        event.preventDefault();
        this.openSubmenu(item);
      });

      backButton?.addEventListener('click', () => {
        this.closeSubmenu(item);
        toggle?.focus();
      });

      item.addEventListener('mouseenter', () => {
        if (this.isDesktop()) this.openSubmenu(item);
      });

      item.addEventListener('focusin', () => {
        if (this.isDesktop()) this.openSubmenu(item);
      });
    }

    handleDetailsToggle() {
      const isOpen = this.details?.open;

      this.details?.classList.toggle('menu-opening', Boolean(isOpen));
      this.headerSection?.classList.toggle('menu-open', Boolean(isOpen));
      document.body.classList.toggle('overflow-hidden-navigation-2', Boolean(isOpen));
      this.summary?.setAttribute('aria-expanded', String(Boolean(isOpen)));

      if (!isOpen) this.closeSubmenus();
      requestAnimationFrame(() => this.updateScrollIndicators());
    }

    handleScroll() {
      this.updateScrollIndicators();
    }

    handleScrollIndicatorClick(event) {
      const indicator = event.currentTarget;
      const target = this.getScrollTarget(indicator);
      if (!target) return;

      target.scrollBy({
        top: Math.max(100, target.clientHeight * 0.35),
        behavior: 'smooth',
      });
    }

    getScrollTarget(indicator) {
      if (indicator.classList.contains('navigation-2__scroll-indicator--main')) return this.menu;
      return indicator.closest('.navigation-2__submenu')?.querySelector('.navigation-2__submenu-content');
    }

    updateScrollIndicators() {
      this.scrollIndicators.forEach((indicator) => {
        const target = this.getScrollTarget(indicator);
        const parentItem = indicator.closest('.navigation-2__menu-item--parent');
        const isMainIndicator = indicator.classList.contains('navigation-2__scroll-indicator--main');
        const isRelevant = isMainIndicator
          ? !this.isDesktop() && !this.parentItems.some((item) => item.classList.contains('is-open'))
          : parentItem?.classList.contains('is-open');
        const canScroll = target && target.scrollHeight - target.clientHeight > 2;
        const isAtEnd = target && target.scrollTop + target.clientHeight >= target.scrollHeight - 2;
        const isVisible = Boolean(this.details?.open && isRelevant && canScroll && !isAtEnd);

        indicator.classList.toggle('is-visible', isVisible);
        indicator.setAttribute('aria-hidden', String(!isVisible));
        indicator.tabIndex = isVisible ? 0 : -1;
      });
    }

    handleDrawerMouseLeave() {
      if (this.isDesktop()) this.closeSubmenus();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;

      const openItem = this.parentItems.find((item) => item.classList.contains('is-open'));
      if (openItem) {
        event.preventDefault();
        this.closeSubmenus();
        openItem.querySelector('.navigation-2__submenu-toggle')?.focus();
        return;
      }

      if (this.details?.open) {
        event.preventDefault();
        this.closeMenu();
      }
    }

    toggleSubmenu(item) {
      if (item.classList.contains('is-open')) {
        this.closeSubmenu(item);
      } else {
        this.openSubmenu(item);
      }
    }

    openSubmenu(item) {
      this.parentItems.forEach((sibling) => {
        if (sibling === item) return;

        sibling.classList.remove('is-open');
        sibling.querySelector('.navigation-2__submenu-toggle')?.setAttribute('aria-expanded', 'false');
        const siblingSubmenu = sibling.querySelector('.navigation-2__submenu');
        if (siblingSubmenu) siblingSubmenu.hidden = this.isDesktop();
      });

      const submenu = item.querySelector('.navigation-2__submenu');

      if (submenu) submenu.hidden = false;

      item.classList.add('is-open');
      item.querySelector('.navigation-2__submenu-toggle')?.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => this.updateScrollIndicators());
    }

    closeSubmenus() {
      this.parentItems.forEach((item) => {
        this.closeSubmenu(item);
      });
    }

    closeSubmenu(item) {
      item.classList.remove('is-open');
      item.querySelector('.navigation-2__submenu-toggle')?.setAttribute('aria-expanded', 'false');
      const submenu = item.querySelector('.navigation-2__submenu');
      if (submenu) submenu.hidden = this.isDesktop();
      requestAnimationFrame(() => this.updateScrollIndicators());
    }

    closeMenu() {
      if (!this.details?.open) return;
      this.details.open = false;
      this.summary?.focus();
    }
  }

  customElements.define('navigation-menu-2', NavigationMenu2);
}
